/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase.ts';

// Fire-and-forget crash reporter. Calls the `reportClientError` Firebase
// callable, which stores the stack trace in Firestore for later review.
// Designed to be safe to invoke from inside an error handler / error boundary:
//   - never throws (a failure here must not trigger another report),
//   - reports for ALL users incl. signed-out (the callable is open, so crashes
//     on the landing / public pages and during sign-in are captured too),
//   - de-dupes identical errors and caps the count per page load, so a render
//     loop can't spam a thousand docs before the server-side limit kicks in.

export interface ClientErrorReportInput {
  message?: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  title?: string;
  source?: 'render' | 'window.onerror' | 'unhandledrejection';
}

const seen = new Set<string>();
let sentThisLoad = 0;
const MAX_PER_LOAD = 5;

const signature = (r: ClientErrorReportInput): string =>
  `${r.source ?? 'render'}::${(r.message ?? '').slice(0, 200)}::${(r.stack ?? '').slice(0, 200)}`;

export const reportClientError = (input: ClientErrorReportInput): void => {
  try {
    if (sentThisLoad >= MAX_PER_LOAD) return;
    const sig = signature(input);
    if (seen.has(sig)) return;
    seen.add(sig);
    sentThisLoad++;

    const payload: ClientErrorReportInput = {
      source: input.source ?? 'render',
      message: input.message,
      stack: input.stack,
      componentStack: input.componentStack,
      url: input.url ?? window.location.href,
      userAgent: input.userAgent ?? navigator.userAgent,
      title: input.title,
    };

    const call = httpsCallable(getFunctions(app, 'us-east4'), 'reportClientError', { timeout: 20000 });
    // Fire-and-forget: swallow any rejection so the reporter can never itself
    // raise an unhandledrejection and re-enter the error path.
    void call(payload).catch(() => {});
  } catch {
    // Defensive: reporting must never throw into the caller's error handler.
  }
};
